import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, inject } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { ConfigService } from '../../shared-services/config.service';

interface PriceStatusRow {
  equipmentLabel: string;
  listPrice: number;
  computedPrice: number;
  recommendedPrice: number;
  tcoStatus?: string;
  engineUptimeStatus?: string;
  priceDynamicsStatus?: string;
}

@Component({
  selector: 'app-grouped-bar-chart-with-status-table',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './grouped-bar-chart-with-status-table.component.html',
  styleUrl: './grouped-bar-chart-with-status-table.component.scss',
})
export class GroupedBarChartWithStatusTableComponent implements OnInit, OnChanges, OnDestroy {
  @Input() data: any;

  loading = false;
  errorMessage = '';
  rows: PriceStatusRow[] = [];
  maxValue = 0;

  private readonly destroy$ = new Subject<void>();
  private readonly configService = inject(ConfigService);

  ngOnInit(): void {
    this.loadData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && !changes['data'].firstChange) {
      this.loadData();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  trackRow(_index: number, row: PriceStatusRow): string {
    return row.equipmentLabel;
  }

  barWidth(value: number): number {
    if (!this.maxValue) {
      return 0;
    }
    return Math.max(6, Math.round((value / this.maxValue) * 100));
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(Number(value || 0));
  }

  statusClass(status?: string): string {
    switch ((status || '').toLowerCase()) {
      case 'good':
      case 'high':
      case 'positive':
        return 'is-good';
      case 'fair':
      case 'medium':
      case 'warning':
        return 'is-fair';
      case 'weak':
      case 'low':
      case 'negative':
        return 'is-weak';
      default:
        return 'is-neutral';
    }
  }

  private loadData(): void {
    this.errorMessage = '';

    if (!this.data?.apiUrl) {
      this.applyRows(this.extractRows(this.data));
      return;
    }

    this.loading = true;
    this.configService
      .getConfig(this.data.apiUrl, this.data, {}, this.data.method)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.applyRows(this.extractRows(response));
          this.loading = false;
        },
        error: () => {
          this.rows = [];
          this.maxValue = 0;
          this.loading = false;
          this.errorMessage = 'Unable to load grouped price analysis data.';
        },
      });
  }

  private applyRows(rows: PriceStatusRow[]): void {
    this.rows = rows;
    this.maxValue = rows.reduce((max, row) => Math.max(max, row.listPrice, row.computedPrice, row.recommendedPrice), 0);
  }

  private extractRows(source: any): PriceStatusRow[] {
    const candidates = [
      source?.priceAnalysisStatus,
      source?.responseList?.[0]?.data?.[0]?.priceAnalysisStatus,
      source?.responseList?.[0]?.data?.priceAnalysisStatus,
      source?.data?.[0]?.priceAnalysisStatus,
      source?.data?.priceAnalysisStatus,
      source?.rows,
    ];

    const rows = candidates.find((candidate) => Array.isArray(candidate));
    if (!Array.isArray(rows)) {
      return [];
    }

    return rows.map((row) => ({
      equipmentLabel: String(row?.equipmentLabel || row?.label || 'Unknown'),
      listPrice: Number(row?.listPrice || 0),
      computedPrice: Number(row?.computedPrice || 0),
      recommendedPrice: Number(row?.recommendedPrice || 0),
      tcoStatus: row?.tcoStatus,
      engineUptimeStatus: row?.engineUptimeStatus,
      priceDynamicsStatus: row?.priceDynamicsStatus,
    }));
  }
}
